import { FontWeights, Toggle } from "@fluentui/react";
import React, { useEffect, useState } from "react";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { DEFAULT_IMAGE_EXTENSION, DEFAULT_IMAGE_RESOLUTION_MAP, ImageDropdown } from "./ImageDropdown";
import { retrieveDocumentProp } from "../../utils/helpers/CustomXmlHelper";
import { fetchDocumentPartDetails } from "../../api/document/DocumentsPart";

interface InheritableImageDropdownProps {
  type: string;
  inherit: boolean;
  imageExtension: string;
  imageResolution: number;
  required: boolean;
  onToggle: (
    type: string,
    inheritImageFormat: boolean,
    imageExtension: string,
    imageResolution: number,
    modified: boolean
  ) => void;
  onExtensionChange: (option: string | number, modified: boolean) => void;
  onResolutionChange: (option: string | number, modified: boolean) => void;
  isFileExcelExisting?: boolean;
  isComponentInstance?: boolean;
}

const customToggleStyles = {
  label: {
    fontWeight: FontWeights.regular,
    display: "inline-block",
    marginRight: "5px",
  },
  container: {
    display: "inline-block",
  },
};

export const InheritableImageDropdown: React.FC<InheritableImageDropdownProps> = ({
  type,
  inherit,
  imageExtension,
  imageResolution,
  required,
  onToggle,
  onExtensionChange,
  onResolutionChange,
  isFileExcelExisting,
  isComponentInstance,
}) => {
  const [documentPart, setDocumentPart] = useState(null);
  const [multiLinked, setMultiLinked] = useState<boolean>(false);
  const [inheritImageFormat, setInheritImageFormat] = useState<boolean>(inherit);
  const [edit, setEdit] = useState(false);

  useEffect(() => {
    const fetchDocumentPart = async () => {
      try {
        const documentId = await retrieveDocumentProp("Document-ID");
        const documentPart = await fetchDocumentPartDetails(documentId);
        setDocumentPart(documentPart);
      } catch (error) {
        // eslint-disable-next-line no-undef
        console.error("Failed to fetch document part:", error);
      }
    };
    fetchDocumentPart();
  }, []);

  useEffect(() => {
    if (inheritImageFormat) {
      if (documentPart) {
        if (documentPart.multiLinked === true) {
          // reset
          setMultiLinked(true);
          setInheritImageFormat(false);
          onToggle(
            type,
            false,
            DEFAULT_IMAGE_EXTENSION,
            DEFAULT_IMAGE_RESOLUTION_MAP.get(DEFAULT_IMAGE_EXTENSION),
            false
          );
        } else {
          const documentMetadata = documentPart.metadata;
          // inherit, use document values
          onToggle(
            type,
            true,
            documentMetadata.formatImage.imageExtension,
            documentMetadata.formatImage.imageResolution,
            edit
          );
        }
      } else {
        // eslint-disable-next-line no-undef
        console.log("Waiting for docPart");
      }
    } else {
      if (edit) {
        // not inherit, use default values
        onToggle(
          type,
          false,
          imageExtension ?? DEFAULT_IMAGE_EXTENSION,
          imageResolution ?? DEFAULT_IMAGE_RESOLUTION_MAP.get(DEFAULT_IMAGE_EXTENSION),
          true
        );
      } else {
        // first rendering, retrieve initial value
        onToggle(
          type,
          false,
          imageExtension ?? DEFAULT_IMAGE_EXTENSION,
          imageResolution ?? DEFAULT_IMAGE_RESOLUTION_MAP.get(DEFAULT_IMAGE_EXTENSION),
          false
        );
      }
    }
    setEdit(false);
  }, [inheritImageFormat, documentPart, imageExtension, imageResolution, ImageDropdown]);

  const handleInheritToggle = (isChecked) => {
    setEdit(true);
    setInheritImageFormat(isChecked);
  };

  return (
    <>
      <div style={{ display: "inline-flex", verticalAlign: "top", float: "right" }}>
        <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
          <Toggle
            styles={customToggleStyles}
            label={formatMessage("cof.createnewobject.inheritformat")}
            checked={inheritImageFormat}
            disabled={multiLinked || !isFileExcelExisting || isComponentInstance}
            onChange={(_event, inheritImageFormat?: boolean) => handleInheritToggle(inheritImageFormat)}
          />
        </div>
      </div>
      <ImageDropdown
        imageExtension={imageExtension}
        imageResolution={imageResolution}
        disabled={inheritImageFormat || !isFileExcelExisting || isComponentInstance}
        required={required}
        onExtensionChange={onExtensionChange}
        onResolutionChange={onResolutionChange}
      />
    </>
  );
};
